import { useState } from 'react';
import {
  Images, Upload, Search, Sparkles, Link2, Loader2, ImageOff, Check, Wand2, X,
} from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import ImageUploader from '@/components/admin/ImageUploader';
import { StockPhotoSearch, type StockPickResult } from '@/components/admin/StockPhotoPicker';
import { useMediaAssets, formatBytes, SOURCE_LABELS, type MediaAsset } from '@/hooks/useMedia';
import { useAiBudget, describeBudget } from '@/hooks/useAiBudget';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * One picker for every image field in the admin.
 *
 * Before this, choosing an image meant a different control on every screen —
 * an uploader here, a bare text input for a URL there (categories, default OG
 * image), nothing at all for the 24 homepage section types that fall through to
 * a raw-JSON textarea. Nowhere could you re-use a file you had already
 * uploaded, which is why buckets fill up with the same photo under six keys.
 *
 * Four tabs, in the order a designer actually reaches for them:
 *   Library — what this site already has
 *   Upload  — a new file, cropped to the slot
 *   Stock   — Unsplash / Pexels / Pixabay, with the licence credit attached
 *   AI      — generate one, with the parameters that make it repeatable
 *
 * `targetWidth`/`targetHeight` come from the asset-spec registry, so the crop
 * frame, the AI dimensions and the stock import all aim at the same size and
 * the person choosing never has to know it.
 */

type Tab = 'library' | 'upload' | 'stock' | 'ai';

export interface AssetPickResult {
  url: string;
  /** Present when the choice carries a licence credit that must be rendered. */
  image_meta?: StockPickResult['image_meta'];
  alt_text?: string | null;
}

export default function AssetPicker({
  isOpen,
  onClose,
  onPick,
  targetWidth,
  targetHeight,
  /** Seeds the stock search and the AI prompt — usually the slot's label. */
  slotLabel,
  /** A ready-made prompt from the asset-spec registry, if the slot has one. */
  promptHint,
  folder = 'cms',
  allowUrl = true,
}: {
  isOpen: boolean;
  onClose: () => void;
  onPick: (result: AssetPickResult) => void;
  targetWidth?: number;
  targetHeight?: number;
  slotLabel?: string;
  promptHint?: string;
  folder?: 'homepage' | 'blog' | 'cms';
  allowUrl?: boolean;
}) {
  const [tab, setTab] = useState<Tab>('library');
  /**
   * An image to generate FROM, handed over by the Library tab's "Vary this".
   *
   * The reference-image path has existed on the API since img2img landed and
   * had no caller anywhere in the frontend, so restyling or iterating on an
   * image you already had was impossible from inside Rumio. This is the door.
   */
  const [varyFrom, setVaryFrom] = useState<MediaAsset | null>(null);

  function finish(result: AssetPickResult) {
    onPick(result);
    onClose();
  }

  function startVariation(asset: MediaAsset) {
    setVaryFrom(asset);
    setTab('ai');
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={slotLabel ? `Choose an image — ${slotLabel}` : 'Choose an image'}
      className="max-w-4xl"
    >
      {targetWidth && targetHeight && (
        <p className="-mt-1 mb-3 text-xs text-gray-500">
          This slot wants {targetWidth} × {targetHeight}. Uploads are cropped to it and
          generated images are made at it.
        </p>
      )}

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        <TabButton active={tab === 'library'} onClick={() => setTab('library')} icon={Images}>
          Library
        </TabButton>
        <TabButton active={tab === 'upload'} onClick={() => setTab('upload')} icon={Upload}>
          Upload
        </TabButton>
        <TabButton active={tab === 'stock'} onClick={() => setTab('stock')} icon={Search}>
          Stock
        </TabButton>
        <TabButton active={tab === 'ai'} onClick={() => setTab('ai')} icon={Sparkles}>
          Generate
        </TabButton>
      </div>

      {tab === 'library' && <LibraryTab onPick={finish} onVary={startVariation} />}

      {tab === 'upload' && (
        <div className="space-y-4">
          <ImageUploader
            value=""
            onChange={(url) => url && finish({ url })}
            label={slotLabel ?? 'Image'}
            targetWidth={targetWidth}
            targetHeight={targetHeight}
          />
          {allowUrl && <UrlTab onPick={finish} />}
        </div>
      )}

      {tab === 'stock' && (
        <StockPhotoSearch
          onPick={(r) => finish({ url: r.url, image_meta: r.image_meta })}
          folder={folder}
          targetWidth={targetWidth}
          targetHeight={targetHeight}
          initialQuery={slotLabel ?? ''}
        />
      )}

      {tab === 'ai' && (
        <GenerateTab
          onPick={finish}
          targetWidth={targetWidth}
          targetHeight={targetHeight}
          folder={folder}
          initialPrompt={
            (typeof varyFrom?.source_meta?.prompt === 'string'
              ? varyFrom.source_meta.prompt
              : null) ?? promptHint ?? ''
          }
          varyFrom={varyFrom}
          onClearVaryFrom={() => setVaryFrom(null)}
        />
      )}
    </Modal>
  );
}

function TabButton({
  active, onClick, icon: Icon, children,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Images;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-sage-600 text-sage-700'
          : 'border-transparent text-gray-500 hover:text-gray-700',
      )}
      aria-current={active ? 'page' : undefined}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {children}
    </button>
  );
}

// ── Library ─────────────────────────────────────────────────────────────────

function LibraryTab({
  onPick, onVary,
}: {
  onPick: (r: AssetPickResult) => void;
  onVary: (asset: MediaAsset) => void;
}) {
  const [q, setQ] = useState('');
  const { data, isLoading } = useMediaAssets({ q: q.trim() || undefined, limit: 36 });

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
            aria-hidden="true"
          />
          <input
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm text-gray-900 focus:border-sage-500 focus:outline-none focus:ring-1 focus:ring-sage-500"
            placeholder="Search what you've already uploaded"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search the media library"
          />
        </div>
      </div>

      {isLoading && (
        <div className="py-10 text-center text-gray-500">
          <Loader2 className="mx-auto h-6 w-6 animate-spin" aria-hidden="true" />
        </div>
      )}

      {!isLoading && (data?.assets.length ?? 0) === 0 && (
        <div className="py-10 text-center text-gray-500">
          <ImageOff className="mx-auto mb-2 h-7 w-7 text-gray-300" aria-hidden="true" />
          {q ? `Nothing matching “${q}”.` : 'Nothing in the library yet. Upload or generate one.'}
        </div>
      )}

      <div className="grid max-h-[420px] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-4">
        {data?.assets.map((asset) => (
          <AssetTile key={asset.id} asset={asset} onPick={onPick} onVary={onVary} />
        ))}
      </div>
    </div>
  );
}

function AssetTile({
  asset, onPick, onVary,
}: {
  asset: MediaAsset;
  onPick: (r: AssetPickResult) => void;
  onVary: (asset: MediaAsset) => void;
}) {
  return (
    // The vary action is a sibling of the pick button, not a child: a button
    // inside a button is invalid HTML and the inner click never fires.
    <div className="group relative">
      {/* Only for generated images. Varying a stock photo produces a derivative
          the photographer's credit no longer describes, which is a licence
          problem rather than a missing feature. */}
      {asset.source === 'ai' && (
        <button
          type="button"
          onClick={() => onVary(asset)}
          className="absolute left-1.5 top-1.5 z-10 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
          title="Generate a variation of this"
          aria-label="Generate a variation of this image"
        >
          <Wand2 className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        onClick={() => onPick({ url: asset.url ?? '', alt_text: asset.alt_text })}
        className="relative block w-full overflow-hidden rounded-lg border border-gray-200 text-left focus:outline-none focus:ring-2 focus:ring-sage-500"
        title={asset.alt_text ?? asset.title ?? asset.key}
      >
        {asset.url ? (
          <img
            src={asset.url}
            alt=""
            loading="lazy"
            className="aspect-[4/3] w-full bg-gray-100 object-cover transition group-hover:scale-105"
          />
        ) : (
          <div className="grid aspect-[4/3] w-full place-items-center bg-gray-100">
            <ImageOff className="h-6 w-6 text-gray-300" aria-hidden="true" />
          </div>
        )}
        <span className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-[11px] text-white">
          <span className="truncate">{SOURCE_LABELS[asset.source]}</span>
          <span className="shrink-0 opacity-80">
            {asset.width && asset.height ? `${asset.width}×${asset.height}` : formatBytes(asset.bytes)}
          </span>
        </span>
        {asset.usage_count > 0 && (
          <span
            className="absolute right-1.5 top-1.5 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] text-white"
            title={`Used in ${asset.usage_count} place${asset.usage_count === 1 ? '' : 's'}`}
          >
            {asset.usage_count}
          </span>
        )}
      </button>
    </div>
  );
}

// ── URL ─────────────────────────────────────────────────────────────────────

function UrlTab({ onPick }: { onPick: (r: AssetPickResult) => void }) {
  const [url, setUrl] = useState('');
  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-500">
        <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
        Or paste a URL
      </label>
      <div className="flex gap-2">
        <input
          className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-sage-500 focus:outline-none focus:ring-1 focus:ring-sage-500"
          placeholder="https://…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <Button
          type="button"
          variant="secondary"
          disabled={!url.trim()}
          onClick={() => onPick({ url: url.trim() })}
        >
          <Check className="h-4 w-4" aria-hidden="true" />
          <span className="ml-1.5">Use</span>
        </Button>
      </div>
      {/* Worth saying out loud: an external URL is outside the library, so it
          has no alt text, no usage tracking, and nothing stops it 404ing later. */}
      <p className="mt-1.5 text-xs text-gray-500">
        A pasted URL isn't stored here — if that site removes the file, the image breaks.
      </p>
    </div>
  );
}

// ── Generate ────────────────────────────────────────────────────────────────

/**
 * The AI tab.
 *
 * `negative_prompt` and `seed` have always been accepted by the image API and
 * were never offered anywhere in the admin, which is what made generation feel
 * like a slot machine: no way to say what you did not want, and no way to
 * reproduce something you liked. The cost line is here for the same reason it
 * is on every other generate button — the person spending should see it.
 */
function GenerateTab({
  onPick, targetWidth, targetHeight, folder, initialPrompt, varyFrom, onClearVaryFrom,
}: {
  onPick: (r: AssetPickResult) => void;
  targetWidth?: number;
  targetHeight?: number;
  folder: 'homepage' | 'blog' | 'cms';
  initialPrompt: string;
  /** Set when the Library tab sent an image here to iterate on. */
  varyFrom?: MediaAsset | null;
  onClearVaryFrom?: () => void;
}) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [negative, setNegative] = useState('');
  const [seed, setSeed] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<{ url: string; seed: number | null } | null>(null);
  const { data: budget } = useAiBudget();
  const costLine = describeBudget(budget);

  async function generate() {
    if (!prompt.trim()) return;
    setBusy(true);
    setError('');
    try {
      // Sent as base64, which is what the image API takes. Reading our own
      // object here rather than handing over a URL keeps the server from
      // making an outbound request on our behalf.
      const reference = varyFrom?.url ? await fetchAsReference(varyFrom.url) : null;

      const res = await api.post<{ url: string; seed: number | null }>('/admin/ai/generate/image', {
        prompt: prompt.trim(),
        ...(negative.trim() ? { negative_prompt: negative.trim() } : {}),
        ...(seed.trim() && Number.isFinite(Number(seed)) ? { seed: Number(seed) } : {}),
        ...(targetWidth ? { width: targetWidth } : {}),
        ...(targetHeight ? { height: targetHeight } : {}),
        ...(reference ? { reference_image: reference } : {}),
        folder,
      });
      setPreview({ url: res.url, seed: res.seed });
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Generation failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      {varyFrom?.url && (
        <div className="flex items-center gap-3 rounded-lg border border-sage-200 bg-sage-50 p-2.5">
          <img src={varyFrom.url} alt="" className="h-14 w-14 shrink-0 rounded object-cover" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-sage-800">Making a variation of this</p>
            <p className="truncate text-xs text-sage-700">
              Edit the prompt to say what should change.
            </p>
          </div>
          <button
            type="button"
            onClick={onClearVaryFrom}
            className="shrink-0 rounded p-1 text-sage-700 hover:bg-sage-100"
            aria-label="Start from scratch instead"
            title="Start from scratch instead"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-500" htmlFor="ap-prompt">
          What should it show?
        </label>
        <textarea
          id="ap-prompt"
          rows={3}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-sage-500 focus:outline-none focus:ring-1 focus:ring-sage-500"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="A sunlit greenhouse interior, shallow depth of field, warm tones"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500" htmlFor="ap-negative">
            Keep out (optional)
          </label>
          <input
            id="ap-negative"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-sage-500 focus:outline-none focus:ring-1 focus:ring-sage-500"
            value={negative}
            onChange={(e) => setNegative(e.target.value)}
            placeholder="text, watermark, people"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500" htmlFor="ap-seed">
            Seed (optional)
          </label>
          <input
            id="ap-seed"
            inputMode="numeric"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-sage-500 focus:outline-none focus:ring-1 focus:ring-sage-500"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder="Same seed + same prompt = same image"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700 dark:border-danger-800 dark:bg-danger-950 dark:text-danger-300">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button type="button" onClick={generate} disabled={busy || !prompt.trim()}>
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          )}
          <span className="ml-2">{preview ? 'Try again' : 'Generate'}</span>
        </Button>
        {costLine && <span className="text-xs text-gray-500">{costLine}</span>}
      </div>

      {preview && (
        <div className="rounded-lg border border-gray-200 p-3">
          <img
            src={preview.url}
            alt=""
            className="mb-3 max-h-64 w-full rounded object-contain"
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-gray-500">
              {preview.seed !== null ? `Seed ${preview.seed}` : 'Saved to your library'}
            </span>
            <Button type="button" onClick={() => onPick({ url: preview.url })}>
              <Check className="h-4 w-4" aria-hidden="true" />
              <span className="ml-1.5">Use this</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Read one of our own images back as the base64 payload the image API expects.
 *
 * Returns null on any failure. A reference image is an enhancement; losing the
 * whole generation because a thumbnail 404'd would be the wrong trade, so the
 * caller falls back to generating from the prompt alone.
 */
async function fetchAsReference(
  url: string,
): Promise<{ media_type: 'image/jpeg' | 'image/png' | 'image/webp'; data: string } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const type = blob.type.split(';')[0].trim().toLowerCase();
    const media_type =
      type === 'image/jpeg' || type === 'image/png' || type === 'image/webp'
        ? (type as 'image/jpeg' | 'image/png' | 'image/webp')
        : 'image/webp';
    const data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
      reader.readAsDataURL(blob);
    });
    return data ? { media_type, data } : null;
  } catch {
    return null;
  }
}
